import React, { useMemo, useState } from 'react';
import DesktopForgotPasswordModal from './DesktopForgotPasswordModal';
import DesktopThreadsBackground from './DesktopThreadsBackground';
import './DesktopLogin.css';
import loopinoLogo from '../../../assets/images/LOOPINO-logo-new.svg';
import useDesktopLoginLogic from './useDesktopLoginLogic';

const DesktopLogin = ({ onLoginSuccess }) => {
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const heroThreadsColor = useMemo(() => [0.98, 0.92, 1], []);
  const {
    t,
    isLogin,
    formData,
    errors,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    touchedFields,
    passwordStrength,
    isProcessing,
    verificationInfo,
    isDevMode,
    isLoading,
    generalMessage,
    isGeneralSuccess,
    formTitle,
    formDescription,
    emailLabel,
    submitLabel,
    togglePrompt,
    toggleAction,
    toggleTitle,
    passwordStrengthLabel,
    googleLabel,
    dividerLabel,
    rememberMeLabel,
    forgotPasswordLabel,
    clearMessageTitle,
    footerCopy,
    footerLinks,
    handleInputChange,
    handleBlur,
    handleSubmit,
    handleDevLogin,
    clearErrorsAndToggle,
    toggleMode,
    clearGeneralMessage,
    handleGoogleLogin
  } = useDesktopLoginLogic({ onLoginSuccess });

  const handleForgotPassword = () => {
    console.log('[DesktopLogin] Opening forgot password modal');
    setForgotPasswordModalOpen(true);
  };

  const handleForgotPasswordSuccess = (email) => {
    console.log('[DesktopLogin] Forgot password success for email:', email);
  };

  return (
    <div className="desktop-login-container">
      <div className="desktop-login-grid">
        <section className="desktop-login-hero" aria-live="polite">
          <div className="desktop-hero-glow" aria-hidden="true" />
          <div className="desktop-login-hero-card">
            <DesktopThreadsBackground
              color={heroThreadsColor}
              amplitude={1}
              distance={0.3}
              enableMouseInteraction
              aria-hidden="true"
            />
              <div className="desktop-hero-content">
              <div className="desktop-hero-branding">
              <img src={loopinoLogo} alt={t('desktop.brand.logoAlt', { defaultValue: 'Loopino' })} />
              </div>
              <h2>{t('login.desktop.tagline', { defaultValue: 'Il tuo social media manager AI powered' })}</h2>
              <p>{t('login.desktop.hero.description')}</p>
              <div className="desktop-hero-chips">
                <span>{t('login.desktop.hero.chips.trends')}</span>
                <span>{t('login.desktop.hero.chips.calendar')}</span>
                <span>{t('login.desktop.hero.chips.ai')}</span>
              </div>
              <div className="desktop-hero-stats">
                <div>
                  <strong>{t('login.desktop.hero.stats.flowTitle')}</strong>
                  <span>{t('login.desktop.hero.stats.flowSubtitle')}</span>
                </div>
                <div>
                  <strong>{t('login.desktop.hero.stats.creativityTitle')}</strong>
                  <span>{t('login.desktop.hero.stats.creativitySubtitle')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="desktop-login-card" aria-live="polite">
          <header className="desktop-login-card-header">
            <div>
              <p className="desktop-login-eyebrow">{t('login.desktop.eyebrow')}</p>
              <h1>{formTitle}</h1>
              <p className="desktop-login-description">{formDescription}</p>
            </div>
            <button
              type="button"
              className="desktop-mode-switch"
              onClick={errors.general ? clearErrorsAndToggle : toggleMode}
              title={toggleTitle}
            >
              {toggleAction}
            </button>
          </header>

          <form onSubmit={handleSubmit} className="desktop-login-form">
            {generalMessage && (
              <div
                className={`desktop-general-message ${
                  isGeneralSuccess ? 'desktop-general-message-success' : 'desktop-general-message-error'
                }`}
              >
                <div className="desktop-general-message-content">
                  <span>{generalMessage}</span>
                  {verificationInfo?.email && (
                    <span className="desktop-general-subtext">
                      {verificationInfo.type === 'registration'
                        ? t('login.messages.verificationEmail', { email: verificationInfo.email })
                        : t('login.messages.verificationPending', { email: verificationInfo.email })}
                    </span>
                  )}
                  {isDevMode && verificationInfo?.token && (
                    <span className="desktop-general-dev-token">
                      {t('login.messages.devTokenLabel')} <code>{verificationInfo.token}</code>
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="desktop-clear-message"
                  onClick={clearGeneralMessage}
                  title={clearMessageTitle}
                >
                  ×
                </button>
              </div>
            )}

            {!isLogin && (
              <>
                <div className="desktop-form-pair">
                  <div className="desktop-form-field" style={{ '--field-index': 0 }}>
                    <div
                      className={`desktop-input-container desktop-floating-label ${
                        formData.firstName ? 'has-value' : ''
                      } ${errors.firstName ? 'error' : ''}`}
                    >
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className="desktop-form-input"
                        placeholder=" "
                        autoComplete="given-name"
                        required
                      />
                      <label className="desktop-floating-label-text">{t('login.fields.firstName')}</label>
                      {formData.firstName && !errors.firstName && (
                        <div className="desktop-input-icon success">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {errors.firstName && touchedFields.firstName && (
                      <span className="desktop-error-message">{errors.firstName}</span>
                    )}
                  </div>

                  <div className="desktop-form-field" style={{ '--field-index': 0.1 }}>
                    <div
                      className={`desktop-input-container desktop-floating-label ${
                        formData.lastName ? 'has-value' : ''
                      } ${errors.lastName ? 'error' : ''}`}
                    >
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className="desktop-form-input"
                        placeholder=" "
                        autoComplete="family-name"
                        required
                      />
                      <label className="desktop-floating-label-text">{t('login.fields.lastName')}</label>
                      {formData.lastName && !errors.lastName && (
                        <div className="desktop-input-icon success">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {errors.lastName && touchedFields.lastName && (
                      <span className="desktop-error-message">{errors.lastName}</span>
                    )}
                  </div>
                </div>

                <div className="desktop-form-field" style={{ '--field-index': 0.2 }}>
                  <div
                    className={`desktop-input-container desktop-floating-label ${
                      formData.username ? 'has-value' : ''
                    } ${errors.username ? 'error' : ''}`}
                  >
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className="desktop-form-input"
                      placeholder=" "
                      autoComplete="username"
                      required
                    />
                    <label className="desktop-floating-label-text">{t('login.fields.username')}</label>
                    {formData.username && !errors.username && (
                      <div className="desktop-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M13.5 4.5L6 12L2.5 8.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.username && touchedFields.username && (
                    <span className="desktop-error-message">{errors.username}</span>
                  )}
                </div>
              </>
            )}

            <div className="desktop-form-field" style={{ '--field-index': 0.3 }}>
              <div
                className={`desktop-input-container desktop-floating-label ${
                  formData.email ? 'has-value' : ''
                } ${errors.email ? 'error' : ''}`}
              >
                <input
                  type={isLogin ? 'text' : 'email'}
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="desktop-form-input"
                  placeholder=" "
                  autoComplete={isLogin ? 'username' : 'email'}
                  required
                />
                <label className="desktop-floating-label-text">{emailLabel}</label>
                {formData.email && !errors.email && (
                  <div className="desktop-input-icon success">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.5 4.5L6 12L2.5 8.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {errors.email && touchedFields.email && (
                <span className="desktop-error-message">{errors.email}</span>
              )}
            </div>

            <div className="desktop-form-field" style={{ '--field-index': 0.4 }}>
              <div
                className={`desktop-input-container desktop-floating-label desktop-password-container ${
                  formData.password ? 'has-value' : ''
                } ${errors.password ? 'error' : ''}`}
              >
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="desktop-form-input"
                  placeholder=" "
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                />
                <label className="desktop-floating-label-text">{t('login.fields.password')}</label>
                <button
                  type="button"
                  className="desktop-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    {showPassword ? (
                      <path
                        d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8zm11 3a3 3 0 100-6 3 3 0 000 6z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ) : (
                      <>
                        <path
                          d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="M4.5 19.5l15-15" stroke="currentColor" strokeWidth="1.5" />
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {errors.password && touchedFields.password && (
                <span className="desktop-error-message">{errors.password}</span>
              )}
              {!isLogin && formData.password && (
                <div className="desktop-password-strength">
                  <div className="desktop-strength-bar">
                    <div
                      className={`desktop-strength-fill desktop-strength-${passwordStrength}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                  <div className="desktop-strength-text">{passwordStrengthLabel}</div>
                </div>
              )}
            </div>

            {!isLogin && (
              <div className="desktop-form-field" style={{ '--field-index': 0.5 }}>
                <div
                  className={`desktop-input-container desktop-floating-label desktop-password-container ${
                    formData.confirmPassword ? 'has-value' : ''
                  } ${errors.confirmPassword ? 'error' : ''}`}
                >
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="desktop-form-input"
                    placeholder=" "
                    autoComplete="new-password"
                    required
                  />
                  <label className="desktop-floating-label-text">
                    {t('login.fields.confirmPassword')}
                  </label>
                  <button
                    type="button"
                    className="desktop-password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      {showConfirmPassword ? (
                        <path
                          d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8zm11 3a3 3 0 100-6 3 3 0 000 6z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      ) : (
                        <>
                          <path
                            d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path d="M4.5 19.5l15-15" stroke="currentColor" strokeWidth="1.5" />
                        </>
                      )}
                    </svg>
                  </button>
                  {formData.confirmPassword &&
                    !errors.confirmPassword &&
                    formData.confirmPassword === formData.password && (
                      <div className="desktop-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M13.5 4.5L6 12L2.5 8.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                </div>
                {errors.confirmPassword && touchedFields.confirmPassword && (
                  <span className="desktop-error-message">{errors.confirmPassword}</span>
                )}
              </div>
            )}

            {isLogin && (
              <div className="desktop-form-options" style={{ '--field-index': 0.55 }}>
                <label className="desktop-checkbox">
                  <input type="checkbox" />
                  <span className="desktop-checkbox-indicator" />
                  {rememberMeLabel}
                </label>
                <button type="button" className="desktop-forgot-password" onClick={handleForgotPassword}>
                  {forgotPasswordLabel}
                </button>
              </div>
            )}

            <div className="desktop-submit-section" style={{ '--field-index': 0.6 }}>
              <button
                type="submit"
                disabled={isLoading}
                className={`desktop-submit-button ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? <div className="desktop-loading-spinner" /> : submitLabel}
              </button>
              {false && process.env.REACT_APP_DEV_MODE === 'true' && (
                <button
                  type="button"
                  className="desktop-dev-login"
                  onClick={handleDevLogin}
                  disabled={isProcessing}
                >
                  DEV login
                </button>
              )}
            </div>

            <div className="desktop-form-divider" style={{ '--field-index': 0.65 }}>
              <span>{dividerLabel}</span>
            </div>

            <button
              type="button"
              className="desktop-google-button"
              style={{ '--field-index': 0.7 }}
              onClick={handleGoogleLogin}
              disabled={isLoading || isProcessing}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M19.99 10.187c0-.82-.069-1.417-.216-2.037H10.2v3.698h5.62c-.113.857-.69 2.182-1.98 3.06l-.02.124 2.88 2.239.2.02c1.83-1.691 2.88-4.17 2.88-7.104z"
                  fill="#4285F4"
                />
                <path
                  d="M10.2 19.93c2.67 0 4.907-.87 6.543-2.37l-3.06-2.383c-.922.62-2.102.985-3.483.985-2.668 0-4.926-1.8-5.733-4.248l-.118.01-2.993 2.32-.04.111c1.617 3.2 4.94 5.575 8.884 5.575z"
                  fill="#34A853"
                />
                <path
                  d="M4.467 11.914c-.216-.64-.336-1.32-.336-2.014s.12-1.374.336-2.014l-.006-.132-3.031-2.36-.099.047C.568 7.045 0 8.446 0 9.9s.568 2.855 1.331 4.259l3.136-2.445z"
                  fill="#FBBC04"
                />
                <path
                  d="M10.2 3.853c1.914 0 3.206.828 3.943 1.521l2.878-2.826C15.1.985 12.87 0 10.2 0 6.256 0 2.933 2.375 1.331 5.641l3.03 2.36C5.169 5.653 7.427 3.853 10.2 3.853z"
                  fill="#EB4335"
                />
              </svg>
              {googleLabel}
            </button>

            <div className="desktop-form-toggle" style={{ '--field-index': 0.75 }}>
              <span>{togglePrompt}</span>
              <button
                type="button"
                onClick={errors.general ? clearErrorsAndToggle : toggleMode}
                className="desktop-toggle-button"
                title={toggleTitle}
              >
                {toggleAction}
              </button>
            </div>
          </form>

          <footer className="desktop-login-footer">
            <p>{footerCopy}</p>
            <div className="desktop-login-footer-links">
              <a href="/privacy">{footerLinks.privacy}</a>
              <a href="/terms">{footerLinks.terms}</a>
              <a href="/cookies">{footerLinks.cookies}</a>
              <a href="/legal">{footerLinks.legal}</a>
              <a href="mailto:support@loopino.com">{footerLinks.help}</a>
            </div>
          </footer>
        </section>
      </div>

      <DesktopForgotPasswordModal
        isOpen={forgotPasswordModalOpen}
        onClose={() => setForgotPasswordModalOpen(false)}
        onSuccess={handleForgotPasswordSuccess}
      />
    </div>
  );
};

export default DesktopLogin;
